import React, { useState, useEffect } from 'react';
import { useApiService } from '../../services/api';
import { 
  Plus, 
  X, 
  Upload, 
  FileText, 
  DollarSign, 
  Calendar,
  CheckCircle,
  XCircle,
  Receipt,
  Paperclip
} from 'lucide-react';
import AttachmentUpload from './AttachmentUpload';
import './AddDeal.css';

const DealForm = ({ isOpen, onClose, onSuccess, deal = null, mode = 'add' }) => {
  const apiService = useApiService();
  const [cars, setCars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const initialForm = {
    carId: '',
    dealType: 'BOTH',
    purchaseInfo: {
      customerId: '',
      value: '',
      dealDate: '',
      filesReceived: false,
      numberPlatesReceived: false
    },
    sellInfo: {
      customerId: '',
      value: '',
      dealDate: '',
      filesReceived: false,
      numberPlatesReceived: false
    },
    purchasePaymentStructures: [],
    sellPaymentStructures: [],
    expenses: '',
    notes: '',
    dealAttachments: []
  };

  const [formData, setFormData] = useState(initialForm);
  const [purchasePaymentForm, setPurchasePaymentForm] = useState({
    amount: '',
    dueDate: '',
    description: '',
    status: 'DUE'
  });
  const [sellPaymentForm, setSellPaymentForm] = useState({
    amount: '',
    dueDate: '',
    description: '',
    status: 'DUE'
  });

  useEffect(() => {
    if (isOpen) {
      loadCars();
      loadCustomers();
      if (mode === 'edit' && deal) {
        populateFormWithDeal(deal);
      } else if (mode === 'add') {
        // Reset form data for add mode
        setFormData(initialForm);
        setPurchasePaymentForm({
          amount: '',
          dueDate: '',
          description: '',
          status: 'DUE'
        });
        setSellPaymentForm({
          amount: '',
          dueDate: '',
          description: '',
          status: 'DUE'
        });
      }
    }
  }, [isOpen, deal, mode]);

  // Debug form data changes
  useEffect(() => {
    console.log('Form data updated:', formData);
    console.log('Purchase payments:', formData.purchasePaymentStructures);
    console.log('Sell payments:', formData.sellPaymentStructures);
  }, [formData]);

  const loadCars = async () => {
    try {
      const response = await apiService.getAllCars({ limit: 1000 });
      setCars(response.cars || []);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await apiService.getAllCustomers({ limit: 1000 });
      setCustomers(response.customers || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const populateFormWithDeal = (deal) => {
    setFormData({
      carId: deal.carId || '',
      dealType: deal.dealType || 'BOTH',
      purchaseInfo: {
        customerId: deal.purchaseInfo?.customerId || '',
        value: deal.purchaseInfo?.value || '',
        dealDate: deal.purchaseInfo?.dealDate ? new Date(deal.purchaseInfo.dealDate).toISOString().split('T')[0] : '',
        filesReceived: deal.purchaseInfo?.filesReceived || false,
        numberPlatesReceived: deal.purchaseInfo?.numberPlatesReceived || false
      },
      sellInfo: {
        customerId: deal.sellInfo?.customerId || '',
        value: deal.sellInfo?.value || '',
        dealDate: deal.sellInfo?.dealDate ? new Date(deal.sellInfo.dealDate).toISOString().split('T')[0] : '',
        filesReceived: deal.sellInfo?.filesReceived || false,
        numberPlatesReceived: deal.sellInfo?.numberPlatesReceived || false
      },
      purchasePaymentStructures: deal.purchasePaymentStructures || [],
      sellPaymentStructures: deal.sellPaymentStructures || [],
      expenses: deal.expenses || '',
      notes: deal.notes || '',
      dealAttachments: deal.dealAttachments || []
    });
  };

  // Validate numeric input (allows digits, one decimal point, and empty string)
  const validateNumericInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    // Allow only digits and one decimal point
    const numericRegex = /^\d*\.?\d*$/;
    if (numericRegex.test(value)) {
      return value;
    }
    return false;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate numeric fields (expenses)
    if (name === 'expenses') {
      const validated = validateNumericInput(value);
      if (validated !== false) {
        setFormData(prev => ({
          ...prev,
          [name]: validated
        }));
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePurchaseInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate numeric fields (value)
    if (name === 'value') {
      const validated = validateNumericInput(value);
      if (validated !== false) {
        setFormData(prev => ({
          ...prev,
          purchaseInfo: {
            ...prev.purchaseInfo,
            [name]: validated
          }
        }));
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      purchaseInfo: {
        ...prev.purchaseInfo,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleSellInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate numeric fields (value)
    if (name === 'value') {
      const validated = validateNumericInput(value);
      if (validated !== false) {
        setFormData(prev => ({
          ...prev,
          sellInfo: {
            ...prev.sellInfo,
            [name]: validated
          }
        }));
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      sellInfo: {
        ...prev.sellInfo,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handlePurchasePaymentInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate numeric fields (amount)
    if (name === 'amount') {
      const validated = validateNumericInput(value);
      if (validated !== false) {
        setPurchasePaymentForm(prev => ({
          ...prev,
          [name]: validated
        }));
      }
      return;
    }
    
    setPurchasePaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSellPaymentInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate numeric fields (amount)
    if (name === 'amount') {
      const validated = validateNumericInput(value);
      if (validated !== false) {
        setSellPaymentForm(prev => ({
          ...prev,
          [name]: validated
        }));
      }
      return;
    }
    
    setSellPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculatePaymentSummary = (payments) => {
    // Convert string amounts to numbers for calculation, but keep as strings in state
    const total = payments.reduce((sum, payment) => {
      const amount = payment.amount ? (typeof payment.amount === 'string' ? parseFloat(payment.amount) || 0 : payment.amount) : 0;
      return sum + amount;
    }, 0);
    const received = payments.filter(payment => payment.status === 'RECEIVED')
      .reduce((sum, payment) => {
        const amount = payment.amount ? (typeof payment.amount === 'string' ? parseFloat(payment.amount) || 0 : payment.amount) : 0;
        return sum + amount;
      }, 0);
    const pending = total - received;
    
    return { total, received, pending };
  };

  const addPurchasePayment = () => {
    console.log('Adding purchase payment:', purchasePaymentForm);
    
    const amountValue = purchasePaymentForm.amount ? (typeof purchasePaymentForm.amount === 'string' ? parseFloat(purchasePaymentForm.amount) : purchasePaymentForm.amount) : 0;
    if (!purchasePaymentForm.amount || amountValue <= 0) {
      alert('Valid amount is required!');
      return;
    }

    const newPayment = {
      id: Date.now(),
      amount: purchasePaymentForm.amount, // Keep as string
      dueDate: purchasePaymentForm.dueDate || null,
      description: purchasePaymentForm.description || '',
      status: purchasePaymentForm.status,
      createdAt: new Date().toISOString()
    };

    console.log('New purchase payment:', newPayment);

    setFormData(prev => {
      const updated = {
        ...prev,
        purchasePaymentStructures: [...prev.purchasePaymentStructures, newPayment]
      };
      console.log('Updated form data:', updated);
      return updated;
    });

    setPurchasePaymentForm({
      amount: '',
      dueDate: '',
      description: '',
      status: 'DUE'
    });
  };

  const addSellPayment = () => {
    console.log('Adding sell payment:', sellPaymentForm);
    
    const amountValue = sellPaymentForm.amount ? (typeof sellPaymentForm.amount === 'string' ? parseFloat(sellPaymentForm.amount) : sellPaymentForm.amount) : 0;
    if (!sellPaymentForm.amount || amountValue <= 0) {
      alert('Valid amount is required!');
      return;
    }

    const newPayment = {
      id: Date.now(),
      amount: sellPaymentForm.amount, // Keep as string
      dueDate: sellPaymentForm.dueDate || null,
      description: sellPaymentForm.description || '',
      status: sellPaymentForm.status,
      createdAt: new Date().toISOString()
    };

    console.log('New sell payment:', newPayment);

    setFormData(prev => {
      const updated = {
        ...prev,
        sellPaymentStructures: [...prev.sellPaymentStructures, newPayment]
      };
      console.log('Updated form data:', updated);
      return updated;
    });

    setSellPaymentForm({
      amount: '',
      dueDate: '',
      description: '',
      status: 'DUE'
    });
  };

  const updatePurchasePaymentStatus = (paymentId, newStatus) => {
    setFormData(prev => ({
      ...prev,
      purchasePaymentStructures: prev.purchasePaymentStructures.map(payment => 
        payment.id === paymentId 
          ? { 
              ...payment, 
              status: newStatus,
              ...(newStatus === 'RECEIVED' && { receivedAt: new Date().toISOString() })
            }
          : payment
      )
    }));
  };

  const updateSellPaymentStatus = (paymentId, newStatus) => {
    setFormData(prev => ({
      ...prev,
      sellPaymentStructures: prev.sellPaymentStructures.map(payment => 
        payment.id === paymentId 
          ? { 
              ...payment, 
              status: newStatus,
              ...(newStatus === 'RECEIVED' && { receivedAt: new Date().toISOString() })
            }
          : payment
      )
    }));
  };

  const removePurchasePayment = (paymentId) => {
    setFormData(prev => ({
      ...prev,
      purchasePaymentStructures: prev.purchasePaymentStructures.filter(payment => 
        payment.id !== paymentId
      )
    }));
  };

  const removeSellPayment = (paymentId) => {
    setFormData(prev => ({
      ...prev,
      sellPaymentStructures: prev.sellPaymentStructures.filter(payment => 
        payment.id !== paymentId
      )
    }));
  };

  const getCarDisplayName = (car) => {
    if (!car) return 'N/A';
    const reg = car.registrationNumber || '';
    const chassis = car.chassisNumber || '';
    const engine = car.engineNumber || '';
    return `${car.name} ${car.vehicleModel} ${reg ? `(${reg})` : chassis ? `(${chassis})` : engine ? `(${engine})` : ''}`.trim();
  };

  const getCustomerDisplayName = (customer) => {
    if (!customer) return 'N/A';
    const identifier = customer.cnic || customer.phoneNumber || 'N/A';
    return `${customer.fullName} (${identifier})`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-PK');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.carId) {
      alert('Car is required!');
      return;
    }
    
    if (!formData.purchaseInfo.customerId) {
      alert('Purchase customer is required!');
      return;
    }

    try {
      setSubmitting(true);
      
      // Clean up the form data - keep values as strings
      const cleanedFormData = {
        ...formData,
        purchaseInfo: {
          ...formData.purchaseInfo,
          value: formData.purchaseInfo.value || null, // Keep as string
          dealDate: formData.purchaseInfo.dealDate || null
        },
        sellInfo: formData.sellInfo.customerId ? {
          ...formData.sellInfo,
          value: formData.sellInfo.value || null, // Keep as string
          dealDate: formData.sellInfo.dealDate || null
        } : null,
        purchasePaymentStructures: formData.purchasePaymentStructures || [],
        sellPaymentStructures: formData.sellPaymentStructures || [],
        expenses: formData.expenses || '0', // Keep as string
        notes: formData.notes || null
      };

      console.log('Submitting deal data:', cleanedFormData);
      console.log('Purchase payments:', cleanedFormData.purchasePaymentStructures);
      console.log('Sell payments:', cleanedFormData.sellPaymentStructures);
      
      if (mode === 'add') {
        await apiService.createDeal(cleanedFormData);
      } else {
        await apiService.updateDeal(deal.id, cleanedFormData);
      }
      
      setFormData(initialForm);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving deal:', err);
      alert(`Failed to ${mode} deal. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const fileNames = files.map(file => file.name);
    
    setFormData(prev => ({
      ...prev,
      dealAttachments: [...prev.dealAttachments, ...fileNames]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      dealAttachments: prev.dealAttachments.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="add-deal-modal-overlay">
      <div className="add-deal-modal">
        <div className="modal-header">
          <h2>{mode === 'add' ? 'Add New Deal' : 'Edit Deal'}</h2>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-deal-form">
          {/* Car Selection */}
          <div className="form-section">
            <h3>Car Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>
                  Car* {mode === 'edit' && <span style={{ color: '#6b7280', fontSize: '0.85em' }}>(locked on edit)</span>}
                </label>
                <select 
                  name="carId" 
                  value={formData.carId} 
                  onChange={handleInputChange} 
                  required
                  disabled={mode === 'edit'}
                >
                  <option value="">Select Car</option>
                  {cars.filter(car => car.dealStatus !== 'Purchased' || (mode === 'edit' && car.id === parseInt(formData.carId))).map(car => (
                    <option key={car.id} value={car.id}>
                      {getCarDisplayName(car)}
                    </option>
                  ))}
                </select>
                {mode === 'edit' && (
                  <small style={{ color: '#6b7280', display: 'block', marginTop: '0.35rem' }}>
                    Car selection is locked after the deal is created.
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Purchase Section */}
          <div className="form-section">
            <h3>Purchase Information*</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Purchased From* (Seller)</label>
                <select 
                  name="customerId" 
                  value={formData.purchaseInfo.customerId} 
                  onChange={handlePurchaseInfoChange} 
                  required
                >
                  <option value="">Select Seller</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {getCustomerDisplayName(customer)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Purchase Value</label>
                <input 
                  type="text" 
                  name="value" 
                  value={formData.purchaseInfo.value} 
                  onChange={handlePurchaseInfoChange} 
                  placeholder="Enter purchase value"
                  inputMode="decimal"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Purchase Date</label>
                <input 
                  type="date" 
                  name="dealDate" 
                  value={formData.purchaseInfo.dealDate} 
                  onChange={handlePurchaseInfoChange} 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="filesReceived" 
                    checked={formData.purchaseInfo.filesReceived} 
                    onChange={handlePurchaseInfoChange} 
                  />
                  Files Received
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="numberPlatesReceived" 
                    checked={formData.purchaseInfo.numberPlatesReceived} 
                    onChange={handlePurchaseInfoChange} 
                  />
                  Number Plates Received
                </label>
              </div>
            </div>

            {/* Purchase Payment Structures */}
            <div className="payments-section">
              <h4>Purchase Payment Structures</h4>
              {(() => {
                const purchaseSummary = calculatePaymentSummary(formData.purchasePaymentStructures);
                return (
                  <div className="payment-summary">
                    <span>Total: {formatCurrency(purchaseSummary.total)}</span>
                    <span>Received: {formatCurrency(purchaseSummary.received)}</span>
                    <span>Pending: {formatCurrency(purchaseSummary.pending)}</span>
                  </div>
                );
              })()}
              
              <div className="payment-form">
                <input
                  type="text"
                  name="amount"
                  placeholder="Amount"
                  value={purchasePaymentForm.amount}
                  onChange={handlePurchasePaymentInputChange}
                  inputMode="decimal"
                />
                <input
                  type="date"
                  name="dueDate"
                  value={purchasePaymentForm.dueDate}
                  onChange={handlePurchasePaymentInputChange}
                />
                <input
                  type="text"
                  name="description"
                  placeholder="Description"
                  value={purchasePaymentForm.description}
                  onChange={handlePurchasePaymentInputChange}
                />
                <select
                  name="status"
                  value={purchasePaymentForm.status}
                  onChange={handlePurchasePaymentInputChange}
                >
                  <option value="DUE">Due</option>
                  <option value="RECEIVED">Received</option>
                  <option value="GIVEN">Given</option>
                </select>
                <button type="button" onClick={(e) => {
                  e.preventDefault();
                  addPurchasePayment();
                }} className="add-btn">
                  <Plus size={16} />
                </button>
              </div>

              <div className="payments-list">
                {formData.purchasePaymentStructures.map((payment) => (
                  <div key={payment.id} className={`payment-item ${payment.status}`}>
                    <div className="payment-info">
                      <div className="payment-amount">{formatCurrency(payment.amount)}</div>
                      <div className="payment-details">
                        {payment.description} {payment.dueDate && `• Due: ${formatDate(payment.dueDate)}`}
                      </div>
                      {payment.status === 'RECEIVED' && payment.receivedAt && (
                        <div className="payment-received">
                          Received: {formatDate(payment.receivedAt)}
                        </div>
                      )}
                    </div>
                    <div className="payment-actions">
                      <select
                        value={payment.status}
                        onChange={(e) => updatePurchasePaymentStatus(payment.id, e.target.value)}
                      >
                        <option value="DUE">Due</option>
                        <option value="RECEIVED">Received</option>
                        <option value="GIVEN">Given</option>
                      </select>
                      <button 
                        type="button" 
                        onClick={() => removePurchasePayment(payment.id)}
                        className="remove-btn"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sale Section */}
          <div className="form-section">
            <h3>Sale Information (Optional)</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Sold To (Buyer)</label>
                <select 
                  name="customerId" 
                  value={formData.sellInfo.customerId} 
                  onChange={handleSellInfoChange}
                >
                  <option value="">Select Buyer (Optional)</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {getCustomerDisplayName(customer)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Sale Value</label>
                <input 
                  type="text" 
                  name="value" 
                  value={formData.sellInfo.value} 
                  onChange={handleSellInfoChange} 
                  placeholder="Enter sale value"
                  inputMode="decimal"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sale Date</label>
                <input 
                  type="date" 
                  name="dealDate" 
                  value={formData.sellInfo.dealDate} 
                  onChange={handleSellInfoChange} 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="filesReceived" 
                    checked={formData.sellInfo.filesReceived} 
                    onChange={handleSellInfoChange} 
                  />
                  Files Received
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="numberPlatesReceived" 
                    checked={formData.sellInfo.numberPlatesReceived} 
                    onChange={handleSellInfoChange} 
                  />
                  Number Plates Received
                </label>
              </div>
            </div>

            {/* Sell Payment Structures */}
            <div className="payments-section">
              <h4>Sell Payment Structures</h4>
              {(() => {
                const sellSummary = calculatePaymentSummary(formData.sellPaymentStructures);
                return (
                  <div className="payment-summary">
                    <span>Total: {formatCurrency(sellSummary.total)}</span>
                    <span>Received: {formatCurrency(sellSummary.received)}</span>
                    <span>Pending: {formatCurrency(sellSummary.pending)}</span>
                  </div>
                );
              })()}
              
              <div className="payment-form">
                <input
                  type="text"
                  name="amount"
                  placeholder="Amount"
                  value={sellPaymentForm.amount}
                  onChange={handleSellPaymentInputChange}
                  inputMode="decimal"
                />
                <input
                  type="date"
                  name="dueDate"
                  value={sellPaymentForm.dueDate}
                  onChange={handleSellPaymentInputChange}
                />
                <input
                  type="text"
                  name="description"
                  placeholder="Description"
                  value={sellPaymentForm.description}
                  onChange={handleSellPaymentInputChange}
                />
                <select
                  name="status"
                  value={sellPaymentForm.status}
                  onChange={handleSellPaymentInputChange}
                >
                  <option value="DUE">Due</option>
                  <option value="RECEIVED">Received</option>
                  <option value="GIVEN">Given</option>
                </select>
                <button type="button" onClick={(e) => {
                  e.preventDefault();
                  addSellPayment();
                }} className="add-btn">
                  <Plus size={16} />
                </button>
              </div>

              <div className="payments-list">
                {formData.sellPaymentStructures.map((payment) => (
                  <div key={payment.id} className={`payment-item ${payment.status}`}>
                    <div className="payment-info">
                      <div className="payment-amount">{formatCurrency(payment.amount)}</div>
                      <div className="payment-details">
                        {payment.description} {payment.dueDate && `• Due: ${formatDate(payment.dueDate)}`}
                      </div>
                      {payment.status === 'RECEIVED' && payment.receivedAt && (
                        <div className="payment-received">
                          Received: {formatDate(payment.receivedAt)}
                        </div>
                      )}
                    </div>
                    <div className="payment-actions">
                      <select
                        value={payment.status}
                        onChange={(e) => updateSellPaymentStatus(payment.id, e.target.value)}
                      >
                        <option value="DUE">Due</option>
                        <option value="RECEIVED">Received</option>
                        <option value="GIVEN">Given</option>
                      </select>
                      <button 
                        type="button" 
                        onClick={() => removeSellPayment(payment.id)}
                        className="remove-btn"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Expenses Section */}
          <div className="form-section">
            <h3>Expenses</h3>
            <div className="form-group">
              <label>Total Expenses</label>
              <input 
                type="text" 
                name="expenses" 
                value={formData.expenses} 
                onChange={handleInputChange} 
                placeholder="Enter total expenses"
                inputMode="decimal"
              />
            </div>
          </div>

          {/* Notes Section */}
          <div className="form-section">
            <h3>Notes</h3>
            <div className="form-group">
              <textarea 
                name="notes" 
                value={formData.notes} 
                onChange={handleInputChange} 
                placeholder="Enter notes about this deal..."
                rows={4}
              />
            </div>
          </div>

          {/* Attachments Section */}
          <div className="form-section">
            <h3>Attachments</h3>
            {deal && deal.id ? (
              <AttachmentUpload dealId={deal.id} />
            ) : (
              <p className="attachment-note">
                Note: Attachments can be uploaded after the deal is created.
              </p>
            )}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="submit-btn">
              {submitting ? (mode === 'add' ? 'Creating...' : 'Updating...') : (mode === 'add' ? 'Create Deal' : 'Update Deal')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DealForm; 